import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { FileSpecificationService } from 'src/app/services/file-specifications/file-specification.service';
import { FileSpecification } from 'src/app/models/file-specifications/file-specification';
import { FileSpecificationVideosSettings } from 'src/app/models/file-specifications/file-specification-settings';
import {
  OutputVideoFormat,
  ALL_OUTPUT_VIDEO_FORMATS,
} from 'src/app/models/file-specifications/output-video-format';
import { VideoBitRate } from 'src/app/models/file-specifications/video-bit-rate';
import {
  FileSpecificationWriteReason,
  FileSpecificationWriteReasonKind,
} from 'src/app/models/file-specifications/file-specification-write-reason';

export interface VideosFileSpecificationUpdateDialogData {
  organizationId: string;
  current: FileSpecification;
}

export type BitRateMode = VideoBitRate['mode'];

type UpdateReasonKind = Exclude<FileSpecificationWriteReasonKind, 'initialCreate' | 'deleted'>;

const SELECTABLE_REASONS: readonly UpdateReasonKind[] = ['preferenceUpdate', 'correction', 'other'];

const REASON_LABELS: Record<UpdateReasonKind, string> = {
  preferenceUpdate: 'Preference update',
  correction: 'Correction',
  other: 'Other',
};

const ALL_BIT_RATE_MODES: readonly BitRateMode[] = ['auto', 'fixed'] as const;

const BIT_RATE_MODE_LABELS: Record<BitRateMode, string> = {
  auto: 'Auto',
  fixed: 'Fixed Mbps',
};

const VIDEO_FORMAT_LABELS: Record<OutputVideoFormat, string> = {
  Mp4H264: 'MP4 H.264',
  Mp4H265: 'MP4 H.265',
  MovH264: 'MOV H.264',
  MovH265: 'MOV H.265',
  WebM: 'WebM',
  Gif: 'GIF',
};

@Component({
  selector: 'app-videos-file-specification-update-dialog',
  templateUrl: './videos-file-specification-update-dialog.component.html',
  styleUrls: ['./videos-file-specification-update-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VideosFileSpecificationUpdateDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly selectableReasons = SELECTABLE_REASONS;
  readonly reasonLabel = (k: UpdateReasonKind): string => REASON_LABELS[k];

  readonly videoFormats = ALL_OUTPUT_VIDEO_FORMATS;
  readonly bitRateModes = ALL_BIT_RATE_MODES;
  readonly formatLabel = (f: OutputVideoFormat): string => VIDEO_FORMAT_LABELS[f];
  readonly bitRateModeLabel = (m: BitRateMode): string => BIT_RATE_MODE_LABELS[m];

  readonly nameControl: FormControl<string>;
  readonly formatControl: FormControl<OutputVideoFormat>;
  readonly widthControl: FormControl<number>;
  readonly heightControl: FormControl<number>;
  readonly bitRateModeControl: FormControl<BitRateMode>;
  readonly mbpsControl: FormControl<number>;

  readonly reasonControl = new FormControl<UpdateReasonKind | null>(null, {
    validators: [Validators.required],
  });
  readonly detailControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(3)],
  });

  submitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: VideosFileSpecificationUpdateDialogData,
    private readonly dialogRef: MatDialogRef<
      VideosFileSpecificationUpdateDialogComponent,
      FileSpecification | undefined
    >,
    private readonly service: FileSpecificationService,
  ) {
    const settings = data.current.value.settings as FileSpecificationVideosSettings;

    this.nameControl = new FormControl<string>(data.current.name, {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(1)],
    });

    this.formatControl = new FormControl<OutputVideoFormat>(settings.format, {
      nonNullable: true,
      validators: [Validators.required],
    });

    this.widthControl = new FormControl<number>(settings.width, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1)],
    });

    this.heightControl = new FormControl<number>(settings.height, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1)],
    });

    this.bitRateModeControl = new FormControl<BitRateMode>(settings.bitRate.mode, {
      nonNullable: true,
      validators: [Validators.required],
    });

    this.mbpsControl = new FormControl<number>(
      settings.bitRate.mode === 'fixed' ? settings.bitRate.mbps : 8,
      { nonNullable: true, validators: [Validators.required, Validators.min(1)] },
    );
  }

  get isOther(): boolean {
    return this.reasonControl.value === 'other';
  }

  get isFixedBitRate(): boolean {
    return this.bitRateModeControl.value === 'fixed';
  }

  get canSubmit(): boolean {
    if (this.submitting) return false;
    if (!this.nameControl.valid) return false;
    if (this.reasonControl.value === null) return false;
    if (this.isOther && !this.detailControl.valid) return false;
    if (!this.formatControl.valid) return false;
    if (!this.widthControl.valid) return false;
    if (!this.heightControl.valid) return false;
    if (!this.bitRateModeControl.valid) return false;
    if (this.isFixedBitRate && !this.mbpsControl.valid) return false;
    return true;
  }

  submit(): void {
    if (!this.canSubmit) return;
    const writeReason = this.buildUpdateReason();
    if (writeReason === null) return;
    this.submitting = true;
    this.service
      .update(this.data.organizationId, this.data.current.id, {
        name: this.nameControl.value.trim(),
        value: { settings: this.buildSettings() },
        writeReason,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: FileSpecification) => this.dialogRef.close(result),
        error: () => {
          this.submitting = false;
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  private buildUpdateReason(): FileSpecificationWriteReason | null {
    const kind = this.reasonControl.value;
    if (kind === null) return null;
    if (kind === 'other') return { kind: 'other', detail: this.detailControl.value.trim() };
    return { kind };
  }

  private buildSettings(): FileSpecificationVideosSettings {
    return {
      media: 'videos',
      format: this.formatControl.value,
      width: this.widthControl.value,
      height: this.heightControl.value,
      bitRate: this.buildBitRate(),
    };
  }

  private buildBitRate(): VideoBitRate {
    if (this.bitRateModeControl.value === 'auto') return { mode: 'auto' };
    return { mode: 'fixed', mbps: this.mbpsControl.value };
  }
}
